import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { CartService } from '../../../core/providers/cart/cart.service';
import { ButtonData, HeroSectionData, TitleInfo } from '../../../common/interfaces';
import { NotificationService } from '../../../core/providers/notification/notification.service';
import { Asset, AssetType } from '../../../common/generated-types';
import { StateService } from '../../../core/providers/state/state.service';
import { ButtonActionType, TextAlignment } from '../../../common/enums';
import { HttpClient } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { navigateByNavigationItem, resolveButtonNavigationItem } from '../../../common/utils/navigation-helpers';

const SWIPE_DELAY_TIME = 0;
const VIDEO_ELEMENT_DELAY_TIME = 100;

@Component({
    selector: 'vsf-hero-section',
    templateUrl: './hero-section.component.html',
    styleUrls: ['./hero-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroSectionComponent implements OnInit, AfterViewInit {
    @Input() heroSection: HeroSectionData;
    @Input() isMobile: boolean;
    @Input() linePadding: string;
    @Input() titleInfo: TitleInfo | null = null;
    @Input() pageFullWidth: boolean = false;
    @Input() sectionMaxHeight: string = '';

    layoutType: 'left-text-full-media' | 'right-text-full-media' | 'middle-text-full-media' | 'custom-text-location-full-media' = 'left-text-full-media';
    title: string;
    subtitle: string;
    textPositionX: number;
    textPositionY: number;
    mobileTextPositionX: number;
    mobileTextPositionY: number;
    textWidth: number;
    textColor: string;
    titleFontSize: string;
    subtitleFontSize: string;
    medias: Asset[];
    button: ButtonData | null;
    buttonPosition: string;
    buttonLocationX: number;
    buttonLocationY: number;
    buttonPositionMobile: string;
    buttonLocationXMobile: number;
    buttonLocationYMobile: number;
    buttonAlignment: TextAlignment;
    buttonAlignmentMobile: TextAlignment;
    shadowOpacity = 0;
    inFlight = false;
    private static readonly DEFAULT_HERO_FONT_SIZE = 40;
    private static readonly DEFAULT_SUBTITLE_FONT_SIZE_DESKTOP = 20;
    private static readonly DEFAULT_VIDEO_ASPECT_RATIO = 16/9;
    private static readonly DEFAULT_MOBILE_HERO_FONT_SIZE_RATIO = 0.75;
    private static readonly DEFAULT_DESKTOP_HERO_WIDTH = 1152;
    private static readonly DEFAULT_DESKTOP_HERO_HEIGHT = 658;
    private static readonly DEFAULT_BUTTON_LOCATION_X = 0.5;
    private static readonly DEFAULT_BUTTON_LOCATION_Y = 0.5;
    mobileHeroFontSizeRatio = 0.4;
    baseUrl = '';
    notificationService: NotificationService;
    private subscription = new Subscription();
    swiperSlideWidth = HeroSectionComponent.DEFAULT_DESKTOP_HERO_WIDTH;
    swiperSlideHeight = HeroSectionComponent.DEFAULT_DESKTOP_HERO_HEIGHT;
    videoAspectRatio: number = HeroSectionComponent.DEFAULT_VIDEO_ASPECT_RATIO;
    videoWidth: number = HeroSectionComponent.DEFAULT_DESKTOP_HERO_WIDTH;
    videoHeight: number = HeroSectionComponent.DEFAULT_DESKTOP_HERO_HEIGHT;
    isSingleVideo: boolean = false;
    isBrowser: boolean;
    isPageHeading: boolean = false;
    h1StyleMode: string = 'auto';
    readyToDisplayVideo$ = new BehaviorSubject<boolean>(false);

    private static readonly BREAK_POINT_CONFIG = [
      { query: '(max-width: 300px)', ratio: 0.3 },
      { query: '(max-width: 400px)', ratio: 0.37 },
      { query: '(max-width: 500px)', ratio: 0.45 },
      { query: '(max-width: 600px)', ratio: 0.57 },
      { query: '(max-width: 700px)', ratio: 0.65 },
      { query: '(min-width: 701px)', ratio: 0.75 }
    ];

    constructor(@Inject(PLATFORM_ID) private platformId: object,
                private changeDetector: ChangeDetectorRef,
                private router: Router,
                private cartService: CartService,
                private stateService: StateService,
                private logger: NGXLogger,
                private elementRef: ElementRef,
                private http: HttpClient,
                private breakpointObserver: BreakpointObserver) {
                  this.isBrowser = isPlatformBrowser(this.platformId);
                }

    async ngOnInit() {
        this.stateService.select(state => state.storePath).subscribe(storePath => {
          this.baseUrl = storePath;
        });
        this.logger.info('Hero Section', JSON.stringify(this.heroSection));
        this.layoutType = this.heroSection?.layoutType;
        this.title = this.heroSection?.title;
        this.subtitle = this.heroSection?.subtitle;
        this.textColor = this.heroSection?.textColor;
        this.isPageHeading = this.titleInfo?.isPageHeading === true;
        this.h1StyleMode = this.titleInfo?.h1StyleMode || 'auto';

        const desktopTitlePx    = this.heroSection?.titleFontSize ?? HeroSectionComponent.DEFAULT_HERO_FONT_SIZE;
        const desktopSubtitlePx = this.heroSection?.subtitleFontSize ?? HeroSectionComponent.DEFAULT_SUBTITLE_FONT_SIZE_DESKTOP;
        this.titleFontSize    = `${desktopTitlePx}px`;
        this.subtitleFontSize = `${desktopSubtitlePx}px`;

        if(this.isMobile){
          if (isPlatformBrowser(this.platformId)) {
            const breakpointQueries = HeroSectionComponent.BREAK_POINT_CONFIG.map(config => config.query);
            this.breakpointObserver.observe(breakpointQueries).subscribe(result => {
                const breakpoints = result.breakpoints;
                const matchedBreakpoint = HeroSectionComponent.BREAK_POINT_CONFIG.find(config => breakpoints[config.query]);
                this.mobileHeroFontSizeRatio = matchedBreakpoint?.ratio ?? HeroSectionComponent.DEFAULT_MOBILE_HERO_FONT_SIZE_RATIO;
                const mobileTitlePxOverride    = this.heroSection?.mobileTitleFontSize && this.heroSection?.mobileTitleFontSize > 0 ;
                const mobileSubtitlePxOverride = this.heroSection?.mobileSubtitleFontSize && this.heroSection?.mobileSubtitleFontSize > 0;
                this.titleFontSize = `${mobileTitlePxOverride ? this.heroSection?.mobileTitleFontSize : Math.round(desktopTitlePx * this.mobileHeroFontSizeRatio)}px`;
                this.subtitleFontSize = `${mobileSubtitlePxOverride ? this.heroSection?.mobileSubtitleFontSize : Math.round(desktopSubtitlePx * this.mobileHeroFontSizeRatio)}px`;
                this.changeDetector.markForCheck();
              });
          } else {
            const mobileRatio = HeroSectionComponent.DEFAULT_MOBILE_HERO_FONT_SIZE_RATIO;
            this.titleFontSize    = `${this.heroSection?.mobileTitleFontSize ?? Math.round(desktopTitlePx * mobileRatio)}px`;
            this.subtitleFontSize = `${this.heroSection?.mobileSubtitleFontSize ?? Math.round(desktopSubtitlePx * mobileRatio)}px`;
          }
        }

        this.medias = this.isMobile ?
          (this.heroSection?.mobileMedias?.length > 0 ? this.heroSection?.mobileMedias : this.heroSection?.medias)
          : this.heroSection?.medias;
        this.shadowOpacity = ( 1.0 - this.heroSection?.shadowTransparencyValue) || this.shadowOpacity;

        this.button = this.heroSection?.button;
        this.buttonPosition = this.heroSection?.buttonPosition;
        this.buttonLocationX = this.heroSection?.buttonLocationX;
        this.buttonLocationY = this.heroSection?.buttonLocationY;
        this.buttonPositionMobile = this.heroSection?.buttonPositionMobile;
        this.buttonLocationXMobile = this.heroSection?.buttonLocationXMobile;
        this.buttonLocationYMobile = this.heroSection?.buttonLocationYMobile;
        this.buttonAlignment = this.heroSection?.buttonAlignment || TextAlignment.Center;
        this.buttonAlignmentMobile = this.heroSection?.buttonAlignmentMobile || TextAlignment.Center;

        this.textPositionX       = this.heroSection?.textPositionX || 0.5;
        this.textPositionY       = this.heroSection?.textPositionY || 0.5;
        this.mobileTextPositionX = this.heroSection?.mobileTextPositionX || 0.5;
        this.mobileTextPositionY = this.heroSection?.mobileTextPositionY || 0.5;
        this.textWidth           = this.heroSection?.textWidth || 0.5;

        this.isSingleVideo = (this.medias?.length == 1 && this.medias[0].type === AssetType.VIDEO && this.medias[0].mimeType === 'video/vimeo');
        if(this.isSingleVideo) {
          const videoUrl = this.medias[0].customFields?.videoUrl || '';
          if(videoUrl) {
            if(this.medias[0].width > 0 && this.medias[0].height > 0) {
              this.videoAspectRatio = this.medias[0].width / this.medias[0].height;
              this.changeDetector.detectChanges();
            } else {
              await this.fetchVideoMetadata(videoUrl);
            }
            this.changeDetector.detectChanges();
          }
        }
        this.changeDetector.markForCheck();
    }

    ngAfterViewInit() {
        if (this.isBrowser) {
          // Delay the execution of getSwiperSlideSize
          setTimeout(async () => {
            if(this.isSingleVideo) {
              const videoDimensions = await this.getVideoDimensions();
              this.videoWidth = videoDimensions?.width || this.videoWidth;
              this.videoHeight = videoDimensions?.height || this.videoHeight;
              this.readyToDisplayVideo$.next(true);
            } else {
              this.getSwiperSlideSize();
            }
            this.changeDetector.detectChanges();
          }, this.isSingleVideo? VIDEO_ELEMENT_DELAY_TIME: SWIPE_DELAY_TIME);
        }
    }

    @HostListener('window:resize', [])
    onResize() {
      if(!this.isSingleVideo) {
        this.getSwiperSlideSize();
      }
    }

    private fetchVideoMetadata(videoUrl: string) {
      const oembedUrl = `https://vimeo.com/api/oembed.json?url=${videoUrl}`;
      this.http.get(oembedUrl).pipe(
          tap((data: any) => {
              if (data && data.width && data.height) {
                  this.videoAspectRatio = data.width / data.height;
                  this.changeDetector.detectChanges();
              }
          }),
          catchError(error => {
              this.logger.error(`Error fetching video metadata:${error}`);
              return of(null);
          })
      ).subscribe();
    }

    getTextContainerStyles(): { [k: string]: string } {
      if (this.layoutType !== 'custom-text-location-full-media') return {};

      const clamp = (v: number | undefined) =>
        Math.min(1, Math.max(0, v ?? 0.5));

      const x = this.isMobile ? clamp(this.mobileTextPositionX) : clamp(this.textPositionX);
      const y = this.isMobile ? clamp(this.mobileTextPositionY) : clamp(this.textPositionY);

      const styles: Record<string, string> = {
        position: 'absolute',
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        transform: 'none',
        'pointer-events': 'none',
        'z-index': '5',
      };

      if (this.textWidth != null) {
        styles['width'] = this.textWidth <= 1 ? `${this.textWidth * 100}%` : `${this.textWidth}px`;
      }

      return styles;
    }

    get textAlignmentClasses(): string[] {
      const align = (this.isMobile ? this.buttonAlignmentMobile : this.buttonAlignment);
      switch (align) {
        case TextAlignment.Left:  return ['items-start','text-left'];
        case TextAlignment.Right: return ['items-end','text-right'];
        default:                  return ['items-center','text-center'];
      }
    }

    getSubtitleStyles(): { [k: string]: string } {
      const styles: Record<string,string> = {
        color: this.textColor,
        'font-size': this.subtitleFontSize,
      };
      if (this.layoutType === 'custom-text-location-full-media' && this.textWidth != null) {
        styles['width'] = this.textWidth <= 1 ? `${this.textWidth * 100}%` : `${this.textWidth}px`;
      }
      return styles;
    }

    get buttonFontSize(): string {
      const raw = this.isMobile ? this.button?.mobileTextSize : this.button?.textSize;
      if (raw == null || raw === 0) {
        return this.isMobile ? '13px' : '16px';
      }
      if (typeof raw === 'number') return `${raw}px`;
      return raw;
    }

    get buttonStyles(): { [k: string]: string } {
      return {
        'background-color': this.button?.backgroundColor ?? '',
        'color': this.button?.textColor ?? '',
        'font-size': this.buttonFontSize,
        'pointer-events': 'auto',
      };
    }

    buttonClick(event: Event) {
        this.logger.debug('Button clicked', this.button);
        if(this.button?.enabled && this.button?.actionType) {
            if(this.button.actionType === ButtonActionType.NavigateTo) {
                const navigationItem = resolveButtonNavigationItem(this.button);
                if (navigationItem) {
                    navigateByNavigationItem(this.router, navigationItem, this.baseUrl);
                }
            } else if(this.button.actionType === ButtonActionType.AddVariantToCart) {
                if(this.button.selectedVariantId) {
                    if(this.button.isVariantCustomizable) {
                        this.router.navigate(
                            [`${this.baseUrl}/dp`, this.button.targetProduct?.slug],
                            { queryParams: { variantid: this.button.selectedVariantId } }
                        );
                    } else {
                        this.addToCartAndOpenDrawer(this.button.selectedVariantId);
                    }
                }
            }
        }
    }

    addToCartAndOpenDrawer(variantId: string) {
        this.inFlight = true;
        this.changeDetector.markForCheck();

        this.subscription.add(
          this.cartService.addToCartAndOpenDrawer(variantId).subscribe({
            next: () => {
              this.inFlight = false;
              this.changeDetector.markForCheck();
            },
            error: err => {
              this.inFlight = false;
              this.logger.error('Error in addToCartAndOpenDrawer', err);
              this.changeDetector.markForCheck();
            },
          }),
        );
    }

    getButtonStyles(): { [key: string]: string } {
      const isCustomPosition = (this.isMobile ? this.buttonPositionMobile : this.buttonPosition) === 'custom';
      if (!isCustomPosition) {
        return {};
      }
      const locationX = (this.isMobile ? this.buttonLocationXMobile : this.buttonLocationX)
          || HeroSectionComponent.DEFAULT_BUTTON_LOCATION_X;
      const locationY = (this.isMobile ? this.buttonLocationYMobile : this.buttonLocationY)
          || HeroSectionComponent.DEFAULT_BUTTON_LOCATION_Y;
      return {
        position: 'absolute',
        left: `${locationX * 100}%`,
        top: `${locationY * 100}%`,
        transform: 'translate(-50%, -50%)',
        'pointer-events': 'auto',
        'z-index': '5',
      };
    }

    getVimeoId(source: string): number {
        const match = source.match(/vimeo\.com\/(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
    }

    private getMeasureTarget(): HTMLElement {
      const host = this.elementRef.nativeElement as HTMLElement;
      return (host.closest('.media-frame') as HTMLElement) || host.parentElement || host;
    }

    private waitForSize(el: HTMLElement): Promise<DOMRectReadOnly> {
      return new Promise(resolve => {
        const rectNow = el.getBoundingClientRect();
        if (rectNow.width > 0) return resolve(rectNow);

        const ro = new ResizeObserver(entries => {
          const cr = entries[0].contentRect;
          if (cr.width > 0) {
            ro.disconnect();
            resolve(el.getBoundingClientRect());
          }
        });
        ro.observe(el);
      });
    }

    async getVideoDimensions() {
      if(!this.isBrowser) {
        return null;
      }
      const target = this.getMeasureTarget();
      const rect = await this.waitForSize(target);

      const width = rect.width || Math.min(window.innerWidth || 0, document.documentElement.clientWidth || 0) || HeroSectionComponent.DEFAULT_DESKTOP_HERO_WIDTH;
      const height = Math.floor(width / this.videoAspectRatio);
      return { width, height };
    }

    // use native elements to calculate the swiper-slide width and height
    getSwiperSlideSize() {
        if(!this.isBrowser) {
            return;
        }
        const rect = this.elementRef.nativeElement.parentElement.getBoundingClientRect();
        this.swiperSlideWidth = rect.width;
        this.swiperSlideHeight = rect.height || this.swiperSlideHeight;
    }
}
